import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GetApiHeadersWithBearer, PostRequest } from '../../../network/NetworkUtils';
import { CREATE_SECURED_CREDIT_CARD, STATUS_SUCCESS_CODE } from '../../../network/URLConstants';
import { retrieveData, appTheme } from '../../../helpers/utils/Utils';
import DynamicCard from '../../CustomComponents/DynamicCard';
import Loader from '../../CustomComponents/Loader';
import CustomAlertModel from '../../CustomComponents/CustomAlertModel';
import HomeLayout from '../../Layout/HomeLayout';
import HomeHeader from '../../CustomComponents/HomeHeader';
import BancorpPrivacyPolicy from '../../OnBoard/Signup/BancorpPrivacyPolicy';
import ElectronicAgreement from '../../OnBoard/Signup/ElectronicAgreement';
import UserAgreement from '../../OnBoard/Signup/UserAgreement';
import ArrowLightBlue from '../../../assets/images/Arrow_light_blue.png';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';

const SecuredCreditTermsAndConditions = () => {
    const { t } = useTranslation();
    useDocumentTitle(t('page_title_secured_credit_terms'));

  
  const navigate = useNavigate();
  const accessTokenToApi = useSelector(state => state.accessToken);
  const cards = useSelector(state => JSON.parse(state.userCards));
  const kudzuPlusTrue = useSelector(state => state.kudzuPlusTrue);
  const continueButtonRef = useRef(null);

  // Apply KudzuPlus theming
  const theme = appTheme(kudzuPlusTrue);

  const [loading, setLoading] = useState(false);
  const [checkboxes, setCheckboxes] = useState({
    securedCreditAgreement: false,
    privacyPolicy: false,
    electronicCommunications: false
  });
  const [showModel, setShowModel] = useState(false);
  const [highlightMsg, setHighlightMsg] = useState('');
  const [headerMsg, setHeaderMsg] = useState('');
  const [subHeaderMsg, setSubHeaderMsg] = useState('');
  const [btn1Exists, setBtn1Exists] = useState(false);
  const [btn2Exists, setBtn2Exists] = useState(false);
  const [btn1Text, setBtn1Text] = useState('');
  const [btn2Text, setBtn2Text] = useState('');
  const [btn1TypeAcpt, setBtn1TypeAcpt] = useState(false);
  const [modelImage, setModelImage] = useState(null);
  const [showPolicys, setShowPolicys] = useState('terms');

  // Conditional styling based on KudzuPlus membership
  const HeadingStyles = kudzuPlusTrue ? 
    "font-serif-pro text-[#94C3E8] lg:text-[31px] md:text-[26px] sm:text-[22px] text-[25px] lg:leading-[38px] md:leading-[33px] sm:leading-[30px] leading-[31px] font-[400]" :
    "font-serif-pro text-[#2965FB] lg:text-[31px] md:text-[26px] sm:text-[22px] text-[25px] lg:leading-[38px] md:leading-[33px] sm:leading-[30px] leading-[31px] font-[400]";
    
  const HighlightMsgStyles = "font-serif-pro modal-title lg:text-[32px] md:text-[28px] sm:text-[23px] text-[27px] lg:leading-[38px] md:leading-[32px] sm:leading-[30px] leading-[31px] font-normal text-[var(--modal-text-heading-clr)] cursor-pointer pointer-events-none";
  
  // Modal styling for KudzuPlus
  const highlightMsgStylesForPlus = "font-serif-pro modal-title lg:text-[32px] md:text-[28px] sm:text-[23px] text-[27px] lg:leading-[38px] md:leading-[32px] sm:leading-[30px] leading-[31px] font-normal text-[#94C3E8] cursor-pointer pointer-events-none";
  const headerMsgStylesForPlus = "font-serif-pro modal-title text-white lg:text-[32px] md:text-[28px] sm:text-[23px] text-[27px] lg:leading-[38px] md:leading-[32px] sm:leading-[30px] leading-[31px] font-[400] cursor-pointer pointer-events-none";
  const subHeaderMsgStylesForPlus = "font-hanken-grotesk modal-title md:text-[18px] sm:text-[14px] text-[16px] font-normal cursor-pointer pointer-events-none text-white";

  // Only the first two checkboxes are mandatory, the third one (electronicCommunications) is optional
  const mandatoryCheckboxesChecked = checkboxes.securedCreditAgreement && checkboxes.privacyPolicy;

  const handleCheckboxChange = (checkboxKey) => {
    setCheckboxes(prev => ({
      ...prev,
      [checkboxKey]: !prev[checkboxKey]
    }));
  };

  useEffect(() => {
    if (mandatoryCheckboxesChecked && continueButtonRef.current) {
      continueButtonRef.current.focus();
    }
  }, [mandatoryCheckboxesChecked]);

  const handleContinueClick = async () => {
    if (!mandatoryCheckboxesChecked) {
      showAlertModal(
        true,
        t('secured_credit_error_title'),
        '',
        t('secured_credit_error_validation'),
        true,
        false,
        'OK',
        '',
        true,
        require('../../../assets/images/warning.png')
      );
      return;
    }

    setLoading(true);
    const deviceId = await retrieveData('updatedDeviceId');
    const headers = await GetApiHeadersWithBearer(deviceId, accessTokenToApi);

    const requestObject = {
      cardKey: cards !== null ? cards[0].CardKey : 0,
      acceptedTerms: true,
      agreementTimestamp: new Date().toISOString()
    };

    const url = CREATE_SECURED_CREDIT_CARD.replace('{CARD_KEY}', cards !== null ? cards[0].CardKey : 0);

    try {
      const response = await PostRequest(url, requestObject, headers);
      const parsedResponse = JSON.parse(response);

      if (parsedResponse.ResponseCode === STATUS_SUCCESS_CODE) {
        navigate('/SecureCredit/FirstTimeUserWizard');
      } else {
        const arrMessages = parsedResponse.Messages;
        const message = arrMessages[0];
        const displayText = message.DisplayText;
        showAlertModal(
          true,
          t('secured_credit_error_title'),
          '',
          t('secured_credit_error_contact'),
          true,
          false,
          t('secured_credit_error_home_button'),
          '',
          true,
          require('../../../assets/images/submit_error.png')
        );
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      showAlertModal(
        true,
        t('secured_credit_error_title'),
        '',
        t('secured_credit_error_contact'),
        true,
        false,
        t('secured_credit_error_home_button'),
        '',
        true,
        require('../../../assets/images/submit_error.png')
      );
    } finally {
      setLoading(false);
    }
  };

  const showAlertModal = (showModel, highlightMsg, headerMsg, subHeaderMsg, btn1Exists, btn2Exists, btn1Text, btn2Text, btn1TypeAcpt, modelImage) => {
    setShowModel(showModel);
    setHighlightMsg(highlightMsg);
    setHeaderMsg(headerMsg);
    setSubHeaderMsg(subHeaderMsg);
    setBtn1Exists(btn1Exists);
    setBtn2Exists(btn2Exists);
    setBtn1Text(btn1Text);
    setBtn2Text(btn2Text);
    setBtn1TypeAcpt(btn1TypeAcpt);
    setModelImage(modelImage);
  };

  const closeModel = () => {
    setShowModel(false);
  };

  const handleClick = (type) => {
    if (type === 'privacy') {
      window.open('https://www.kudzumoney.com/policies/bancorp-privacy', '_blank');
    } else if (type === 'electronic') {
      window.open('https://www.kudzumoney.com/policies/electronic-communications-agreement', '_blank');
    } else if (type === 'secured_agreement') {
      window.open('https://www.kudzumoney.com/policies/user-agreement', '_blank');
    }
    setShowPolicys('terms');
  };

  const onAcptBtnClick = () => {
    closeModel();
    navigate('/home');
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleNoThanksClick = () => {
    navigate('/home');
  };

  return (
    <HomeLayout>
      {loading && <Loader />}

      <HomeHeader
        heading={
          <span style={{
            fontFamily: 'Source Serif Pro',
            fontWeight: '400',
            fontSize: '32px',
            lineHeight: '38px',
            letterSpacing: '0%'
          }}>
            {t('secured_credit_terms_next_text')} <span style={{ color: kudzuPlusTrue ? '#94C3E8' : '#94C3E8' }}>{t('secured_credit_terms_review_action_text')}</span>
          </span>
        }
        isHeaderCaption={true}
      />

      <div className="flex flex-col lg:w-[860px] md:w-[860px] sm:w-[464px] w-full max-w-full px-4 sm:px-0 animate__animated animate__fadeInRight">
        <DynamicCard
          header={false}
          footer={false}
          CardRadius={true}
          cardHeight="content-fit"
          HeaderContent={false}
          isWhite={!kudzuPlusTrue}
          isDarkForKudzu={kudzuPlusTrue}
        >
          <div className="mt-16 px-4 sm:px-6">
            <p className="font-serif-pro font-[400] text-[32px] leading-[38px] mb-4 sm:mb-6" style={{ letterSpacing: '0%' }}>
              <span className={kudzuPlusTrue ? "text-[#94C3E8]" : "text-[#2965FB]"}>{t('secured_credit_approved_exclamation')}</span>
              <br />
              <span className={kudzuPlusTrue ? "text-white" : "text-[#262B3A]"}>{t('secured_credit_approved_continue')}</span>
            </p>

            <p className={`font-hanken-grotesk font-[500] text-[20px] leading-[100%] mb-4 sm:mb-6 align-middle ${kudzuPlusTrue ? "text-white" : "text-[#262B3A]"}`} style={{ letterSpacing: '0%' }}>
              {t('secured_credit_terms_review_text')}
            </p>

            <div className="space-y-4 w-full max-w-full">

              <div className="flex items-start">
                <input
                  id="secured-credit-agreement-checkbox"
                  name="secured-credit-terms"
                  type="checkbox"
                  className="checkbox-custom-organic checkbox-no-shadow checkbox-signup-terms mt-1"
                  value="securedCreditAgreement"
                  checked={checkboxes.securedCreditAgreement}
                  onChange={() => handleCheckboxChange('securedCreditAgreement')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleCheckboxChange('securedCreditAgreement');
                    }
                  }}
                  autoFocus
                  tabIndex="0"
                />
                <label
                  htmlFor="secured-credit-agreement-checkbox"
                  className={`font-hanken-grotesk ml-3 sm:ml-4 text-xs sm:text-sm md:text-sm lg:text-base font-normal md:font-[540] ${kudzuPlusTrue ? "text-white" : "text-[#262B3A]"}`}
                >
                  {t('secured_credit_checkbox1_text')}
                  <span
                    className={`ml-1 font-bold cursor-pointer ${kudzuPlusTrue ? "text-[#94C3E8]" : "text-[#2965FB]"}`}
                    onClick={() => handleClick("electronic")}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleClick('electronic')
                      }
                    }}
                    tabIndex="0"
                  >
                    {t('secured_credit_electronic_communications')}
                  </span>
                  {' '}and
                  <span
                    className={`ml-1 font-bold cursor-pointer ${kudzuPlusTrue ? "text-[#94C3E8]" : "text-[#2965FB]"}`}
                    onClick={() => handleClick("privacy")}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleClick('privacy')
                      }
                    }}
                    tabIndex="0"
                  >
                    {t('secured_credit_bancorp_privacy')}
                  </span>.
                </label>
              </div>

              <div className="flex items-start">
                <input
                  id="privacy-policy-checkbox"
                  name="privacy-policy-terms"
                  type="checkbox"
                  className="checkbox-custom-organic checkbox-no-shadow checkbox-signup-terms mt-1"
                  value="privacyPolicy"
                  checked={checkboxes.privacyPolicy}
                  onChange={() => handleCheckboxChange('privacyPolicy')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleCheckboxChange('privacyPolicy');
                    }
                  }}
                  tabIndex="0"
                />
                <label
                  htmlFor="privacy-policy-checkbox"
                  className={`font-hanken-grotesk ml-3 sm:ml-4 text-xs sm:text-sm md:text-sm lg:text-base font-normal md:font-[540] ${kudzuPlusTrue ? "text-white" : "text-[#262B3A]"}`}
                >
                  {t('secured_credit_checkbox2_text')}
                  <span
                    className={`ml-1 font-bold cursor-pointer ${kudzuPlusTrue ? "text-[#94C3E8]" : "text-[#2965FB]"}`}
                    onClick={() => handleClick("secured_agreement")}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleClick('secured_agreement')
                      }
                    }}
                    tabIndex="0"
                  >
                    {t('secured_credit_agreement')}
                  </span>.
                </label>
              </div>

              <div className="flex items-start">
                <input
                  id="electronic-communications-checkbox"
                  name="electronic-communications-terms"
                  type="checkbox"
                  className="checkbox-custom-organic checkbox-no-shadow checkbox-signup-terms mt-1"
                  value="electronicCommunications"
                  checked={checkboxes.electronicCommunications}
                  onChange={() => handleCheckboxChange('electronicCommunications')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleCheckboxChange('electronicCommunications');
                    }
                  }}
                  tabIndex="0"
                />
                <label
                  htmlFor="electronic-communications-checkbox"
                  className={`font-hanken-grotesk ml-3 sm:ml-4 text-xs sm:text-sm md:text-sm lg:text-base font-normal md:font-[540] ${kudzuPlusTrue ? "text-white" : "text-[#262B3A]"}`}
                >
                  {t('secured_credit_checkbox3_text')}
                </label>
              </div>

            </div>

            <div className="pt-6 pb-4">
              <p
                className={`font-hanken-grotesk font-[600] text-[13px] leading-[100%] text-justify ${kudzuPlusTrue ? "text-white" : "text-[#262B3A]"}`}
                style={{
                  width: '540px',
                  height: '42px',
                  letterSpacing: '0%',
                  opacity: 1
                }}
                dangerouslySetInnerHTML={{ __html: t('secured_credit_confirmation_text') }}
              />
            </div>

            <div className="pb-4">
              <button
                ref={continueButtonRef}
                type="button"
                className={`font-hanken-grotesk lg:text-[18px] md:text-[17px] sm:text-[15px] text-[16px] w-full font-medium lg:h-[45px] md:h-[40px] sm:h-[35px] h-[40px] flex items-center justify-center pt-2 pb-1 px-4 ${
                  mandatoryCheckboxesChecked 
                    ? (kudzuPlusTrue ? 'bg-[#94C3E8] hover:bg-[#94C3E8] text-white' : 'bg-submit-btn hover:bg-submit-btn text-white')
                    : 'bg-[#94C3E8] text-white cursor-not-allowed opacity-50'
                  }`}
                onClick={handleContinueClick}
                disabled={!mandatoryCheckboxesChecked}
              >
                {t('secured_credit_submit_application')}
              </button>
            </div>

            <div className="flex justify-center pb-4 sm:pb-6">
              <button
                type="button"
                className={`font-hanken-grotesk font-[500] text-sm sm:text-base lg:text-[16px] md:text-[15px] cursor-pointer ${
                  kudzuPlusTrue ? 'text-[#94C3E8]' : 'text-[#2965FB]'
                }`}
                onClick={handleNoThanksClick}
              >
                {t('SECURED_CREDIT_NO_THANKS')}
              </button>
            </div>
          </div>
        </DynamicCard>
      </div>

      {showModel && (
        <CustomAlertModel
          isOpen={showModel}
          onClose={closeModel}
          image={modelImage}
          highlightMsg={
            <>
              <span className={kudzuPlusTrue ? highlightMsgStylesForPlus : "font-serif-pro modal-title lg:text-[32px] md:text-[28px] sm:text-[23px] text-[27px] lg:leading-[38px] md:leading-[32px] sm:leading-[30px] leading-[31px] font-normal text-[#2965FB] cursor-pointer pointer-events-none"}>{t('secured_credit_error_title_blue')}</span>
              <br />
              <span className={HighlightMsgStyles}>{t('secured_credit_error_title_text')}</span>
            </>
          }
          headerMsg={<p className={HighlightMsgStyles}>{headerMsg}</p>}
          subHeaderMsg={
            <p className={`font-hanken-grotesk ${kudzuPlusTrue ? "text-white" : "text-[#262B3A]"}`}>
              {t('secured_credit_error_contact_prefix')} <a href="tel:1-855-751-0025" className={kudzuPlusTrue ? "text-[#94C3E8]" : "text-[#2965FB]"}>{t('secured_credit_error_phone')}</a> {t('secured_credit_error_contact_middle')} <a href="mailto:support@kudzu.com" className={kudzuPlusTrue ? "text-[#94C3E8]" : "text-[#2965FB]"}>{t('secured_credit_error_email')}</a> {t('secured_credit_error_contact_suffix')}
            </p>
          }
          btn1Exists={btn1Exists}
          btn2Exists={btn2Exists}
          btn1Text={btn1Text}
          btn2Text={btn2Text}
          btn1TypeAcpt={btn1TypeAcpt}
          btn1BgColor={kudzuPlusTrue ? "#94C3E8" : "#2965FB"}
          onAcptBtnClick={onAcptBtnClick}
          verificationSuccess={onAcptBtnClick}
          verificationFailure={closeModel}
        />
      )}
    </HomeLayout>
  );
};

export default SecuredCreditTermsAndConditions;